import {
    ApprovalBudgetKind,
    ApprovalPurchaseOrderKind,
    ApproveBudgetKind,
    ApproveKind,
    ApprovePurchaseOrderKind,
    Decimal,
    Ulid,
    ValueRequirement,
} from "@kase/my-marathon-communication";
import { format } from "date-fns";
import {
    Account,
    ApprovalBudget,
    ApprovalExpense,
    ApprovalInvoice,
    ApprovalPurchaseOrder,
    Employee,
    ExpenseReceipt,
    Project,
    PurchaseCode,
    PurchaseOrder,
    VatClass,
    getReceiptConfig,
} from "~/core";
import { getOcrLevel } from "~/core/color-manager";
import { CostCentre, CostObject } from "~/core/slices/api/cost/interfaces";
import { IDecimalInternal } from "~/core/slices/api/utils/interfaces";
import { store } from "~/core/store";
import { dateToApi, getCodeString, prettyNum, uniq } from "~/utils";
import {
    ApprovalItemViewWarning,
    ApprovalView,
    ApprovalViewStatus,
} from "./interfaces";

const TODAY = new Date();

export const parseGetParams = () => {
    const params = new URLSearchParams(window.location.search);
    const approval = params.get("approval");

    return {
        approval,
    };
};

export const getDefaultExpand = () => {
    const params = new URLSearchParams(window.location.search);
    const approval = params.get("approval");

    return approval ? [approval] : [];
};

export const formatDueDate = (date: Date | null) => {
    if (!date) {
        return "N/A";
    }

    if (date.getFullYear() === TODAY.getFullYear()) {
        return format(date, "dd MMM");
    }

    return format(date, "dd MMM yyyy");
};

export const statusToString = (status: ApprovalViewStatus) => {
    if (status === ApprovalViewStatus.Booked) {
        return "Booked";
    } else if (status === ApprovalViewStatus.Active) {
        return "Open";
    } else if (status === ApprovalViewStatus.Approved) {
        return "Approved";
    } else if (status === ApprovalViewStatus.Cancelled) {
        return "Cancelled";
    } else if (status === ApprovalViewStatus.Declined) {
        return "Declined";
    }

    return "Unknown";
};

export const approvalKindString = (
    approval:
        | ApprovalExpense
        | ApprovalInvoice
        | ApprovalBudget
        | ApprovalPurchaseOrder,
) => {
    if (approval.type === "budget") {
        if (approval.kind === ApproveBudgetKind.BudgetKindNone) {
            return "None";
        } else if (approval.kind === ApproveBudgetKind.BudgetKindApproved) {
            return "Approved";
        } else if (approval.kind === ApproveBudgetKind.BudgetKindRejected) {
            return "Rejected";
        }
    } else if (approval.type === "purchaseorder") {
        if (approval.kind === ApprovePurchaseOrderKind.PurchaseOrderKindNone) {
            return "None";
        } else if (
            approval.kind === ApprovePurchaseOrderKind.PurchaseOrderKindApproved
        ) {
            return "Approved";
        } else if (
            approval.kind === ApprovePurchaseOrderKind.PurchaseOrderKindRejected
        ) {
            return "Rejected";
        }
    } else if (approval.kind === ApproveKind.None) {
        return "None";
    } else if (approval.kind === ApproveKind.PreApproval) {
        return "Pre-approval";
    } else if (approval.kind === ApproveKind.FinalApproval) {
        return "Final approval";
    } else if (approval.kind === ApproveKind.NotMine) {
        return "Not mine";
    } else if (approval.kind === ApproveKind.DoNotPay) {
        return "Do not pay";
    }

    return "Unknown";
};

export type ApprovalColor = "gray" | "blue" | "green" | "red" | "transparent";

export const getApprovalColor = (
    approval:
        | ApprovalExpense
        | ApprovalInvoice
        | ApprovalBudget
        | ApprovalPurchaseOrder,
): ApprovalColor => {
    if (approval.type === "budget") {
        if (approval.kind === ApproveBudgetKind.BudgetKindNone) {
            return "gray";
        }

        if (approval.kind === ApproveBudgetKind.BudgetKindRejected) {
            return "red";
        }

        return approval.approval_kind === ApprovalBudgetKind.BudgetKindInternal
            ? "blue"
            : "green";
    }

    if (approval.type === "purchaseorder") {
        if (approval.kind === ApprovePurchaseOrderKind.PurchaseOrderKindNone) {
            return "gray";
        }

        if (
            approval.kind === ApprovePurchaseOrderKind.PurchaseOrderKindRejected
        ) {
            return "red";
        }

        return approval.approval_kind ===
            ApprovalPurchaseOrderKind.PurchaseOrderKindInternal
            ? "blue"
            : "green";
    }

    if (approval.kind === ApproveKind.None) {
        return "gray";
    }

    if (approval.kind === ApproveKind.PreApproval) {
        return "blue";
    }

    if (approval.kind === ApproveKind.FinalApproval) {
        return "green";
    }

    if (approval.kind === ApproveKind.DoNotPay) {
        return "red";
    }

    return "transparent";
};

export const formatProject = (project: Project | null) =>
    project
        ? `${project.client} ${getCodeString(project.project)} - ${project.name}`
        : "-";

export const formatPurchaseCode = (code: PurchaseCode | null) =>
    code ? `${getCodeString(code.code)} - ${code.name_global}` : "-";

export const formatPurchaseOrder = (order: PurchaseOrder | null) =>
    order ? `${getCodeString(order.code, 3)} ${order.supplier?.name}` : "-";

export const formatVatClass = (vatClass: VatClass | null) =>
    vatClass
        ? `${vatClass.percent ? `(${vatClass.percent.toNumber()}%)` : ""} ${vatClass.code} - ${vatClass.name}`
        : "-";

export const formatAccount = (account: Account | null) =>
    account ? `${account.code} - ${account.name}` : "-";

export const formatCostObject = (costObject: CostObject | null) =>
    costObject ? `${costObject.code} - ${costObject.name}` : "-";

export const formatCostCentre = (costCentre: CostCentre | null) =>
    costCentre ? `${costCentre.code} - ${costCentre.name}` : "-";

export const formatIDecimal = (
    value: IDecimalInternal | null | undefined,
    currencyId?: Readonly<Ulid> | null,
) => {
    if (!value) {
        return "-";
    }

    const code = store.api.currency.get(currencyId)?.code ?? "";
    const prefix = code ? `${code} ` : "";

    return `${prefix}${prettyNum(Decimal.from(value).toNumber())}`;
};

export const formatPeriodDate = (date: Date | null) => {
    if (!date) {
        return "-";
    }

    return format(date, "dd MMM yyyy");
};

export const formatNumber = (
    value: number | null | undefined,
    currencyId?: Readonly<Ulid> | null,
) => {
    if (value === null || value === undefined) {
        return "-";
    }

    const code = store.api.currency.get(currencyId)?.code ?? "";
    const prefix = code ? `${code} ` : "";

    return `${prefix}${prettyNum(value)}`;
};

const order = {
    [ApproveKind.DoNotPay]: 0,
    [ApproveKind.FinalApproval]: 1,
    [ApproveKind.PreApproval]: 2,
    [ApproveKind.None]: 3,
    [ApproveKind.NotMine]: 4,
    [ApproveKind.UNRECOGNIZED]: 5,
};

const budgetOrder = {
    [ApproveBudgetKind.BudgetKindRejected]: 0,
    [ApproveBudgetKind.BudgetKindApproved]: 1,
    [ApproveBudgetKind.BudgetKindNone]: 2,
    [ApproveBudgetKind.UNRECOGNIZED]: 3,
};

export const toSortedApprovals = (
    approvals: (
        | ApprovalExpense
        | ApprovalInvoice
        | ApprovalBudget
        | ApprovalPurchaseOrder
    )[],
) => {
    const type = approvals[0]?.type;
    const src: Record<number, number> =
        type === "budget" || type === "purchaseorder" ? budgetOrder : order;

    return approvals.toSorted((a, b) => src[a.kind] - src[b.kind]);
};

export const calcPosted = (row: ApprovalView | null | undefined) => {
    if (!row || row.type !== "invoice") {
        return [0, 0, 0] as const;
    }

    const purchasePosted = row.raw.purchases.value.reduce(
        (acc, p) => acc.add(p.purchase_price),
        new Decimal(0),
    );
    const costPosted = row.raw.costs.value.reduce(
        (acc, c) => acc.add(c.purchase_price),
        new Decimal(0),
    );
    const totalPosted = purchasePosted.add(costPosted);
    const amount = row.raw.amount
        ? new Decimal(row.raw.amount)
        : new Decimal(0);

    return [
        amount.toNumber(),
        totalPosted.toNumber(),
        amount.subtract(totalPosted).toNumber(),
    ] as const;
};

export const normalizeOcrKeys = (keys: string[]) =>
    uniq(
        keys.map((key) =>
            key === "type" || key === "comment" ? "category" : key,
        ),
    );

export const getOcrIssues = (
    receipt: ExpenseReceipt,
    config?: ReturnType<typeof getReceiptConfig>,
) => {
    const api = store.api;
    const settings = api.settings.user.store.peek().expense;
    const ocr = api.expense.ocr.getFor(receipt._attachments[0]?.toString());
    const ocrCurrency = api.currency.getByCode(
        ocr?.figuresMap.currency?.value ?? "",
    );
    const allTypes = api.expense.mapCodes(ocr?.raw.spsc ?? []).map((t) => t.id);
    const ocrChanged = {
        date: receipt.date !== dateToApi(ocr?.figuresMap.date?.value),
        total: receipt.amount.toNumber() !== ocr?.figuresMap.total?.value,
        vat: receipt.base_vat.toNumber() !== ocr?.figuresMap.vat?.value,
        comment: receipt.comment !== ocr?.figuresMap.category?.value,
        type: !allTypes.includes(receipt.expense_type.toString()),
        currency: receipt.currency.toString() !== ocrCurrency?.id,
    };
    const ocrIgnore = {
        comment:
            !settings.ocr_category_as_description ||
            !receipt.comment ||
            config?.comment === ValueRequirement.Forbidden,
        type: allTypes.length === 0,
        vat: config?.vat === ValueRequirement.Forbidden,
    };
    const { category, ...rest } = ocr?.figuresMap ?? {};
    const map = {
        ...rest,
        type: category,
        comment: category,
    };
    const inaccurate = Object.entries(map)
        .filter(
            ([key, figure]) =>
                figure &&
                getOcrLevel(figure.score) === "red" &&
                !ocrChanged[key as keyof typeof ocrChanged] &&
                !ocrIgnore[key as keyof typeof ocrIgnore],
        )
        .map(([key]) => key);
    const mismatch = Object.entries(map)
        .filter(
            ([key, figure]) =>
                figure &&
                getOcrLevel(figure.score) === "green" &&
                ocrChanged[key as keyof typeof ocrChanged] &&
                !ocrIgnore[key as keyof typeof ocrIgnore],
        )
        .map(([key]) => key);

    return [inaccurate, mismatch] as const;
};

export const getReceiptWarning = (receipt: ExpenseReceipt) => {
    const api = store.api;
    const duplicates = api.expense.duplicate.getMany(
        receipt._attachments.map((a) => a.toString()),
    );
    const combined = api.expense.ocr.combined(
        receipt._attachments.map((a) => a.toString()),
    );
    const config = getReceiptConfig(receipt);
    const [inaccurate, mismatch] = getOcrIssues(receipt, config);

    const duplicate = duplicates.length > 0;
    const db = duplicate
        ? ApprovalItemViewWarning.Duplicate
        : combined.duplicate
          ? ApprovalItemViewWarning.PossibleDuplicate
          : 0;
    const ob = inaccurate.length
        ? ApprovalItemViewWarning.InaccurateOcr
        : mismatch.length
          ? ApprovalItemViewWarning.MismatchOcr
          : 0;

    return db | ob;
};

export const isApprovalDisabled = (approval: ApprovalView) =>
    approval.status === ApprovalViewStatus.Booked ||
    approval.status === ApprovalViewStatus.Cancelled;

export const cmpEmployee = (
    a:
        | ApprovalExpense
        | ApprovalInvoice
        | ApprovalBudget
        | ApprovalPurchaseOrder,
    b: Employee | null,
) => {
    if (a.type === "budget" || a.type === "purchaseorder") {
        return b?._id.equals(a.approver) ?? false;
    }

    return a.user === b?.preferred_username;
};
